import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from "react-router-dom";
import { getNavbarHeight, getViewportDimensions } from '../../../models/Utils';
import { getVBNIndex } from '../../../models/Utils';
import {
  setHasVisitedVBN
} from '../../../store/reducers/mainReducer';

import CreditsWrapper from '../../BuildingBrighterFutures/subcomponents/CreditsWrapper/CreditsWrapper';
import ExhibitMenu from '../../Common/ExhibitMenu/ExhibitMenu';
import SectionIntroSlide from '../subcomponents/SectionIntroSlide/SectionIntroSlide';
import InfoColumns from '../subcomponents/InfoColumns/InfoColumns';
import VbnVideo from '../subcomponents/VbnVideo/VbnVideo';
import DataGrid from '../subcomponents/DataGrid/DataGrid';

import { ADAARIARoles, ADALandmarks } from '../../../models/ADA';
import './VbnSectionPage.scss';

const mobileScreenThreshold = 767; // MUST BE IN SYNC WITH CSS

const VbnSectionPage = () => {
  const dispatch = useDispatch();
  const creditsElement = useRef(null);
  const afterSlideEl = useRef(null);
  const isMobileScreen = useRef(false);
  const lastScreenWidth = useRef(-1);

  const initialized = useSelector(state => state.data.initialized);
  const cmsData = useSelector(state => state.data.cms.verizonByTheNumbers);
  const navbar = useSelector(state => state.data.menus.verizonByTheNumbers);
  const cmsMediaPath = useSelector(state => state.config.basePath + state.config.apiMediaPath);
  const [index, setIndex] = useState(-1);
  const { sectionId } = useParams();
  const history = useHistory();

  const sectionColors = ['red', 'yellow', 'blue'];

  const sectionIndex = getVBNIndex(sectionId, cmsData, history);
  const theSection = cmsData && cmsData.Sections ? cmsData.Sections[sectionIndex] : null;

  useEffect(() => {
    dispatch(setHasVisitedVBN(true));
  }, []);

  const handleResize = () => {
    const viewport = getViewportDimensions();
    if (viewport.width !== lastScreenWidth.current) {
      // On Android devices, it appears that scrolling launches resize event on some devices
      // So we check whether the width of the screen was changed, as we only care about that for layout
      isMobileScreen.current = viewport.width <= mobileScreenThreshold;
      document.body.scrollTo(0, 0);
      lastScreenWidth.current = viewport.width;
    }
  };

  useLayoutEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if(!initialized || !theSection || sectionIndex < 0){
    return null;
  }

  if (theSection.InfoColumns) {
    theSection.InfoColumns.Columns.forEach(column => {
      if (column.ColumnImage && column.ColumnImage.Image && column.ColumnImage.Image.indexOf(cmsMediaPath) < 0) {
        column.ColumnImage.Image = cmsMediaPath + column.ColumnImage.Image;
      }
    });
  }

  if (theSection.IntroSlide) {
    if (theSection.IntroSlide.VideoTitleDesktop && theSection.IntroSlide.VideoTitleDesktop.indexOf(cmsMediaPath) < 0) {
      theSection.IntroSlide.VideoTitleDesktop = cmsMediaPath + theSection.IntroSlide.VideoTitleDesktop;
    }
    if (theSection.IntroSlide.VideoTitleMobile && theSection.IntroSlide.VideoTitleMobile.indexOf(cmsMediaPath) < 0) {
      theSection.IntroSlide.VideoTitleMobile = cmsMediaPath + theSection.IntroSlide.VideoTitleMobile;
    }
  }

  if (theSection.DataGrid) {
    if (theSection.DataGrid.Boxes) {
      theSection.DataGrid.Boxes.forEach((box) => {
        if (box.Icon && box.Icon.indexOf(cmsMediaPath) < 0) {
          box.Icon = cmsMediaPath + box.Icon;
        }
      });
    }
  }

  const goDownFromIntro = () => {
    const start = document.body.scrollTop;
    const end = afterSlideEl.current.getBoundingClientRect().y + document.body.scrollTop - getNavbarHeight();
    const speed = 500;
    const t0 = Date.now();
    const tFinal = t0 + (Math.abs(end - start) / 1000) * speed;
    const animate = () => {
      let t = Date.now();
      if (t >= tFinal) t = tFinal;
      let tNormalized = (t - t0) / (tFinal - t0);
      const distDiff = (end - start) * (--tNormalized * tNormalized * tNormalized + 1); // easeOutCubic
      document.body.scrollTo(0, start + distDiff);
      if (t < tFinal) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  };
  
  // Navigation is circular
  const nextSectionIndex = (sectionIndex + 1) % cmsData.Sections.length;
  const nextSectionId = cmsData.Sections[nextSectionIndex];

  return (
    <div className="vbn-section-page" role={ADALandmarks.Main}>
      <ExhibitMenu title={theSection ? theSection.Name : ""} links={navbar}/>
      <SectionIntroSlide
        title1={theSection.IntroSlide.Title1}
        title2={theSection.IntroSlide.Title2}
        videoTitleDesktop={theSection.IntroSlide.VideoTitleDesktop}
        videoTitleMobile={theSection.IntroSlide.VideoTitleMobile}
        text={theSection.IntroSlide.Subtitle}
        onGoDown={goDownFromIntro}
      />
      {
        theSection.DataGrid
        &&
        <DataGrid
          ref={afterSlideEl}
          data={theSection.DataGrid}
        />
      }
      {
        theSection.InfoVideo && theSection.InfoVideo.Title != null && theSection.InfoVideo.Title !== ""
        &&
        <VbnVideo
          data={theSection.InfoVideo}
        />
      }
      {
        (theSection.InfoColumns && theSection.InfoColumns.Active)
        &&
        <InfoColumns
          data={theSection.InfoColumns}
        />
      }
      <div className="credits-el" ref={creditsElement} >
        <CreditsWrapper 
          next={`/${cmsData.Path.Text}/${nextSectionId.Menu.Path}`}
          title={`Up next: ${cmsData.Sections[nextSectionIndex].Name}`}
          subtitle={cmsData.Sections[nextSectionIndex].FooterText}
        />
      </div>
    </div>
  );
};

export default VbnSectionPage;
